import React, { useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { ImagePlus, Trash2, KeyRound, Copy, ShieldCheck } from 'lucide-react';
import { adminUsuariosAPI } from '../../utils/adminUsuariosAPI';
import { sucessoComHorario } from '../../utils/toastComHorario';
import ModalRodape from './ModalRodape';

const BADGE_TIPO = {
  admin: 'bg-indigo-100 text-indigo-700',
  gerenciador: 'bg-blue-50 text-blue-700'
};

const inputClasses = 'w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-[#40A1E6] focus:ring-2 focus:ring-[#40A1E6]/15 outline-none transition-all';
const labelClasses = 'block text-sm font-medium text-gray-700 mb-1.5';

// Duas abas internas — perfil (nome/e-mail/foto) e senha — pelo mesmo
// motivo do modal de criar enquete: são duas preocupações diferentes que
// não precisam de dois botões/modais separados na lista, mas também não
// deviam ficar tudo misturado numa tela só. Só abre pra quem pode editar
// (dono da conta ou admin) — checagem já feita antes de abrir, em
// GerenciarUsuarios.js.
const EditarUsuarioModal = ({ usuario, sessaoAdmin, onFechar, onAtualizado }) => {
  const [aba, setAba] = useState('perfil');

  const inputRef = useRef(null);
  const [enviandoFoto, setEnviandoFoto] = useState(false);
  // usuario.foto (caminho bruto) vem da listagem de Usuários; usuario.tem_foto
  // (booleano) vem de /auth/me — aceita os dois formatos, já que este modal
  // também é reaberto a partir da sessão logada (editar o próprio perfil,
  // via AdminLayout).
  const [temFoto, setTemFoto] = useState(!!(usuario.foto || usuario.tem_foto));
  const [fotoVersion, setFotoVersion] = useState(0);

  const [nomeCompleto, setNomeCompleto] = useState(usuario.nome_completo);
  const [email, setEmail] = useState(usuario.email);
  const [salvandoPerfil, setSalvandoPerfil] = useState(false);

  const [resetando, setResetando] = useState(false);
  const [senhaGerada, setSenhaGerada] = useState(null);
  const [senhaAtual, setSenhaAtual] = useState('');
  const [novaSenha, setNovaSenha] = useState('');
  const [alterandoSenha, setAlterandoSenha] = useState(false);

  const ehProprioUsuario = usuario.id === sessaoAdmin?.id;

  const selecionarArquivo = () => inputRef.current?.click();

  const onArquivoSelecionado = async (e) => {
    const arquivo = e.target.files?.[0];
    if (!arquivo) return;

    setEnviandoFoto(true);
    try {
      await adminUsuariosAPI.enviarFoto(usuario.id, arquivo);
      sucessoComHorario('Foto atualizada');
      setTemFoto(true);
      setFotoVersion((v) => v + 1);
      onAtualizado();
    } catch (error) {
      toast.error(error.userMessage || 'Erro ao enviar foto');
    } finally {
      setEnviandoFoto(false);
      e.target.value = '';
    }
  };

  const removerFoto = async () => {
    try {
      await adminUsuariosAPI.removerFoto(usuario.id);
      sucessoComHorario('Foto removida');
      setTemFoto(false);
      onAtualizado();
    } catch (error) {
      toast.error(error.userMessage || 'Erro ao remover foto');
    }
  };

  const salvarPerfil = async (e) => {
    e.preventDefault();
    if (!nomeCompleto.trim() || !email.trim()) return;
    setSalvandoPerfil(true);
    try {
      await adminUsuariosAPI.atualizarPerfil(usuario.id, { nomeCompleto: nomeCompleto.trim(), email: email.trim() });
      sucessoComHorario('Dados atualizados');
      onAtualizado();
    } catch (error) {
      toast.error(error.userMessage || 'Erro ao salvar');
    } finally {
      setSalvandoPerfil(false);
    }
  };

  const resetarSenha = async () => {
    if (!window.confirm(`Gerar uma nova senha temporária para "${usuario.email}"? A senha atual deixa de funcionar.`)) return;
    setResetando(true);
    try {
      const senha = await adminUsuariosAPI.resetarSenha(usuario.id);
      setSenhaGerada(senha);
      sucessoComHorario('Senha redefinida');
    } catch (error) {
      toast.error(error.userMessage || 'Erro ao resetar senha');
    } finally {
      setResetando(false);
    }
  };

  const copiarSenha = () => {
    navigator.clipboard?.writeText(senhaGerada);
    sucessoComHorario('Senha copiada');
  };

  const alterarSenha = async (e) => {
    e.preventDefault();
    if (novaSenha.length < 8) {
      toast.error('A nova senha precisa ter ao menos 8 caracteres');
      return;
    }
    setAlterandoSenha(true);
    try {
      await adminUsuariosAPI.alterarSenhaPropria(usuario.id, { senhaAtual, novaSenha });
      sucessoComHorario('Senha alterada');
      setSenhaAtual('');
      setNovaSenha('');
    } catch (error) {
      toast.error(error.userMessage || 'Erro ao alterar senha');
    } finally {
      setAlterandoSenha(false);
    }
  };

  return (
    <div>
      <div className="flex items-center gap-3 mb-5">
        <div className="w-12 h-12 rounded-full bg-primary-100 flex items-center justify-center text-primary-700 font-semibold overflow-hidden flex-shrink-0">
          {temFoto ? (
            <img key={fotoVersion} src={`${adminUsuariosAPI.fotoUrl(usuario.id)}?v=${fotoVersion}`} alt="" className="w-full h-full object-cover" />
          ) : (
            usuario.nome_completo?.[0]?.toUpperCase() || '?'
          )}
        </div>
        <div className="min-w-0">
          <p className="font-semibold text-gray-900 truncate">{usuario.nome_completo}</p>
          <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full ${BADGE_TIPO[usuario.tipo]}`}>
            {usuario.tipo === 'admin' && <ShieldCheck className="w-3 h-3" />} {usuario.tipo}
          </span>
        </div>
      </div>

      <div className="flex items-center gap-1 bg-gray-100 rounded-xl p-1 mb-5">
        <button
          type="button"
          onClick={() => setAba('perfil')}
          className={`flex-1 text-sm font-medium py-2 rounded-lg transition-colors ${
            aba === 'perfil' ? 'bg-white shadow-sm text-gray-900' : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          Perfil
        </button>
        <button
          type="button"
          onClick={() => setAba('senha')}
          className={`flex-1 text-sm font-medium py-2 rounded-lg transition-colors ${
            aba === 'senha' ? 'bg-white shadow-sm text-gray-900' : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          Senha
        </button>
      </div>

      {aba === 'perfil' && (
        <div className="space-y-4">
          <input
            ref={inputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            className="hidden"
            onChange={onArquivoSelecionado}
          />
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={selecionarArquivo}
              disabled={enviandoFoto}
              className="flex items-center gap-1.5 text-sm text-gray-600 hover:bg-gray-100 rounded-lg px-3 py-2 border border-gray-200"
            >
              <ImagePlus className="w-3.5 h-3.5" /> {enviandoFoto ? 'Enviando...' : temFoto ? 'Trocar foto' : 'Adicionar foto'}
            </button>
            {temFoto && (
              <button
                type="button"
                onClick={removerFoto}
                className="flex items-center gap-1.5 text-sm text-red-500 hover:bg-red-50 rounded-lg px-3 py-2"
              >
                <Trash2 className="w-3.5 h-3.5" /> Remover
              </button>
            )}
          </div>

          <form onSubmit={salvarPerfil} className="space-y-4">
            <div>
              <label htmlFor="editar-usuario-nome" className={labelClasses}>Nome completo</label>
              <input id="editar-usuario-nome" value={nomeCompleto} onChange={(e) => setNomeCompleto(e.target.value)} className={inputClasses} />
            </div>
            <div>
              <label htmlFor="editar-usuario-email" className={labelClasses}>E-mail</label>
              <input id="editar-usuario-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} className={inputClasses} />
            </div>
            <div className="flex justify-center">
              <button type="submit" disabled={salvandoPerfil} className="btn-primary">
                {salvandoPerfil ? 'Salvando...' : 'Salvar alterações'}
              </button>
            </div>
          </form>
        </div>
      )}

      {aba === 'senha' && (
        ehProprioUsuario ? (
          <form onSubmit={alterarSenha} className="space-y-3">
            <p className="text-xs text-gray-500">Pra trocar sua própria senha, confirme a atual.</p>
            <input
              type="password"
              placeholder="Senha atual"
              value={senhaAtual}
              onChange={(e) => setSenhaAtual(e.target.value)}
              className={inputClasses}
              required
            />
            <input
              type="password"
              placeholder="Nova senha (mínimo 8 caracteres)"
              value={novaSenha}
              onChange={(e) => setNovaSenha(e.target.value)}
              className={inputClasses}
              required
              minLength={8}
            />
            <button
              type="submit"
              disabled={alterandoSenha}
              className="flex items-center gap-1.5 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-lg px-3 py-2 transition-colors disabled:opacity-50"
            >
              <KeyRound className="w-3.5 h-3.5" /> {alterandoSenha ? 'Alterando...' : 'Alterar senha'}
            </button>
          </form>
        ) : (
          <div>
            <p className="text-xs text-gray-500 mb-3">
              Você não sabe (nem deveria saber) a senha atual de {usuario.nome_completo.split(' ')[0]} — gere uma temporária e repasse por fora.
            </p>
            {!senhaGerada ? (
              <button
                onClick={resetarSenha}
                disabled={resetando}
                className="flex items-center gap-1.5 text-sm font-medium text-amber-700 bg-amber-50 hover:bg-amber-100 rounded-lg px-3 py-2 transition-colors"
              >
                <KeyRound className="w-3.5 h-3.5" /> {resetando ? 'Gerando...' : 'Resetar senha'}
              </button>
            ) : (
              <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3">
                <p className="text-xs text-emerald-700 mb-2">
                  Senha temporária gerada — copie e repasse agora. Ela só aparece aqui, uma vez.
                </p>
                <div className="flex items-center gap-2">
                  <code className="flex-1 bg-white border border-emerald-200 rounded px-2.5 py-1.5 text-sm font-mono text-gray-900">
                    {senhaGerada}
                  </code>
                  <button
                    onClick={copiarSenha}
                    className="flex-shrink-0 w-8 h-8 flex items-center justify-center text-emerald-700 hover:bg-emerald-100 rounded-lg"
                    aria-label="Copiar senha"
                  >
                    <Copy className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}
          </div>
        )
      )}

      <ModalRodape>
        <button
          onClick={onFechar}
          className="text-sm font-medium text-gray-600 hover:bg-gray-100 rounded-lg px-4 py-2.5 transition-colors"
        >
          Fechar
        </button>
      </ModalRodape>
    </div>
  );
};

export default EditarUsuarioModal;
